import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { RideHistoryListResponse, RidesFilters } from './rides.types'

/** Filtres → params HTTP, en omettant les valeurs vides. */
export function toParams(filters: RidesFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: filters.page,
    limit: filters.limit,
  }
  if (filters.status) params.status = filters.status
  if (filters.from) params.from = filters.from
  if (filters.to) params.to = filters.to
  if (filters.driverId) params.driverId = filters.driverId
  if (filters.passengerId) params.passengerId = filters.passengerId
  return params
}

/** Historique paginé. `keepPreviousData` évite le flash vide entre deux pages. */
export function useRides(filters: RidesFilters) {
  return useQuery<RideHistoryListResponse>({
    queryKey: ['admin', 'rides', filters],
    queryFn: async () => {
      const { data } = await api.get<RideHistoryListResponse>('/admin/rides', {
        params: toParams(filters),
      })
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}

/** Extrait le filename de l'en-tête Content-Disposition, sinon repli daté. */
function filenameFromHeader(header: unknown): string {
  if (typeof header === 'string') {
    const match = header.match(/filename="?([^"]+)"?/)
    if (match) return match[1]
  }
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  return `rides_export_${date}.csv`
}

/**
 * Télécharge l'export CSV (toutes les lignes filtrées, pagination ignorée).
 * Le CSV est généré côté serveur ; on ne fait que déclencher le download.
 */
export function useExportRides() {
  return useMutation({
    mutationFn: async (filters: RidesFilters) => {
      // page/limit retirés : l'export n'est pas paginé (toutes les lignes filtrées).
      const exportParams = toParams(filters)
      delete exportParams.page
      delete exportParams.limit
      const response = await api.get('/admin/rides/export', {
        params: exportParams,
        responseType: 'blob',
      })

      const filename = filenameFromHeader(response.headers['content-disposition'])
      const url = URL.createObjectURL(response.data as Blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
  })
}
